'use client';

import { useEffect, useState } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { translatePrintLabel } from '@/lib/utils/print-label-translations';
import { formatMetricSizeLabel } from '@/lib/utils/dimensions';
import { OptionPriceDelta } from './option-price-delta';
import { shouldShowInlineCustomSizeInputs } from '@/lib/product-options/custom-size-rendering';

/** "38 x 14 mm - Colop Printer 20" wordt maat boven, model eronder in klein. */
function splitSizeLabel(label: string): [string, string] {
  const index = label.indexOf(' - ');
  if (index <= 0) return [label, ''];
  return [label.slice(0, index).trim(), label.slice(index + 3).trim()];
}

interface SizeValue {
  id: string;
  label: string;
  value: string;
  priceModifier?: number;
  width?: number;
  height?: number;
  metadata?: {
    hidden?: boolean;
    orientation?: 'portrait' | 'landscape' | 'square' | 'custom' | null;
    shape?: string;
    printShape?: string;
  };
}

interface GroupedSizeSelectorProps {
  values: SizeValue[];
  selectedValue: string;
  onChange: (value: string) => void;
  optionId: string;
  invalidValues?: Set<string>;
  locale?: string;
  groupByOrientation?: boolean;
  /**
   * Bounds Print.com publishes for the custom size, in millimetres, plus the
   * current values. Without these the custom tab offered a button and no way to
   * type a size, so a product advertising "Eigen formaat" could not be ordered
   * at one.
   */
  customSizeLimits?: { minWidth?: number; maxWidth?: number; minHeight?: number; maxHeight?: number };
  customWidth?: number | string;
  customHeight?: number | string;
  onCustomSizeChange?: (dimension: 'width' | 'height', value: string) => void;
  /** Uitleg dat de eigen maat het totale (bij verpakkingen: uitgevouwen) drukvlak is. */
  customSizeHint?: string;
}

type SizeOrientation = 'portrait' | 'landscape' | 'square' | 'custom' | null;

function deriveOrientation(value: SizeValue): SizeOrientation {
  if (value.value === 'custom' || value.metadata?.orientation === 'custom') {
    return 'custom';
  }

  const shape = `${value.metadata?.shape || ''} ${value.metadata?.printShape || ''}`.toLowerCase();
  if (shape.includes('hexagon') || shape.includes('circle')) {
    return null;
  }

  const roundText = `${value.label || ''} ${value.value || ''}`;
  const lowerText = roundText.toLowerCase();
  if (
    lowerText.includes('circular') ||
    lowerText.includes('circle') ||
    lowerText.includes('round') ||
    lowerText.includes('rond') ||
    lowerText.includes('cirkel') ||
    lowerText.includes('diameter') ||
    lowerText.includes('ø') ||
    lowerText.includes('⌀')
  ) {
    return null;
  }

  const metadataWidth = Number(value.width);
  const metadataHeight = Number(value.height);
  if (Number.isFinite(metadataWidth) && Number.isFinite(metadataHeight) && metadataWidth > 0 && metadataHeight > 0) {
    if (metadataWidth > metadataHeight) return 'landscape';
    if (metadataHeight > metadataWidth) return 'portrait';
    return 'square';
  }

  const text = `${value.label || ''} ${value.value || ''}`;
  const match = text.match(/(\d+(?:[.,]\d+)?)\s*(?:x|X|×|_)\s*(\d+(?:[.,]\d+)?)/i);

  if (match) {
    const width = Number(match[1].replace(',', '.'));
    const height = Number(match[2].replace(',', '.'));

    if (Number.isFinite(width) && Number.isFinite(height)) {
      if (width > height) return 'landscape';
      if (height > width) return 'portrait';
      return 'square';
    }
  }

  return value.metadata?.orientation || null;
}

const NAMED_SHAPES: Array<[string, RegExp]> = [
  ['circle', /circular|circle|round|rond|cirkel|[⌀øØ]/i],
  ['heart', /heart|hart/i],
  ['oval', /oval|ovaal/i],
  ['hexagon', /hexagon|zeshoek/i],
  ['rectangle', /rectangle|rechthoek/i],
  ['square', /square|vierkant/i],
];

/**
 * Een product waarvan de maten eigenlijk vormen zijn (patch: rond, hart,
 * ovaal, rechthoek, vierkant). Groeperen op staand/liggend/vierkant zet daar
 * een hart onder "Liggend" en liet de ronde patch zonder tab, waardoor de
 * klant alleen vierkant kon kiezen. Zulke maten tonen we als één raster.
 */
export function sizeValuesAreShapes(values: Array<{ label?: string; value?: string }>) {
  const shapes = new Set<string>();
  for (const value of values) {
    const text = `${value.label || ''} ${value.value || ''}`;
    for (const [shape, pattern] of NAMED_SHAPES) if (pattern.test(text)) { shapes.add(shape); break; }
  }
  return shapes.size >= 3;
}

function formatVirtualCustomSize(value: string) {
  if (value.startsWith('rotate:')) {
    const [, , width, height] = value.split(':');
    if (!width || !height) return null;
    return `${width} x ${height} cm`;
  }

  if (!value.startsWith('custom:')) return null;
  const [, width, height] = value.split(':');
  if (!width || !height) return null;
  return `${width} x ${height} cm`;
}

function apparelSizeRank(value: SizeValue) {
  const raw = String(value.label || value.value || '').toUpperCase().trim();
  const normalized = raw
    .replace(/\bSIZE\b|\bMAAT\b|\bTAILLE\b|\bGRÖSSE\b/gi, '')
    .replace(/[^A-Z0-9]/g, '');

  const baseOrder: Record<string, number> = {
    XXS: 10,
    XS: 20,
    S: 30,
    M: 40,
    L: 50,
    XL: 60,
    XXL: 70,
    XXXL: 80,
  };

  if (baseOrder[normalized] !== undefined) return baseOrder[normalized];

  const numericXl = normalized.match(/^([2-9])XL$/);
  if (numericXl) return 50 + Number(numericXl[1]) * 10;

  const youth = normalized.match(/^(Y|YOUTH|KIDS?|CHILD)(XS|S|M|L|XL)$/);
  if (youth) return (baseOrder[youth[2]] ?? 999) - 0.5;

  return null;
}

function numericDimensionRank(value: SizeValue) {
  const width = Number(value.width);
  const height = Number(value.height);
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { area: width * height, width, height };
  }

  const text = `${value.label || ''} ${value.value || ''}`.replace(/×/g, 'x');
  const match = text.match(/(\d+(?:[.,]\d+)?)\s*(?:x|X|_)\s*(\d+(?:[.,]\d+)?)/);
  if (!match) return null;
  const parsedWidth = Number(match[1].replace(',', '.'));
  const parsedHeight = Number(match[2].replace(',', '.'));
  if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) return null;
  return { area: parsedWidth * parsedHeight, width: parsedWidth, height: parsedHeight };
}

function sortSizeValues(values: SizeValue[]) {
  return values
    .map((value, index) => ({ value, index }))
    .sort((a, b) => {
      const apparelA = apparelSizeRank(a.value);
      const apparelB = apparelSizeRank(b.value);
      if (apparelA !== null && apparelB !== null) return apparelA - apparelB;
      if (apparelA !== null) return -1;
      if (apparelB !== null) return 1;

      const dimensionA = numericDimensionRank(a.value);
      const dimensionB = numericDimensionRank(b.value);
      if (dimensionA && dimensionB) {
        return dimensionA.area - dimensionB.area
          || dimensionA.width - dimensionB.width
          || dimensionA.height - dimensionB.height
          || a.index - b.index;
      }

      return a.index - b.index;
    })
    .map((entry) => entry.value);
}

export function GroupedSizeSelector({
  values,
  selectedValue,
  onChange,
  optionId,
  customSizeLimits,
  customWidth,
  customHeight,
  onCustomSizeChange,
  customSizeHint,
  invalidValues = new Set(),
  locale = 'nl',
  groupByOrientation = true,
}: GroupedSizeSelectorProps) {
  // Filter out hidden sizes
  const visibleSizes = sortSizeValues(values
    .filter(v => !v.metadata?.hidden && !invalidValues.has(String(v.value)))
    .map(v => ({
      ...v,
      metadata: {
        ...(v.metadata || {}),
        orientation: deriveOrientation(v)
      }
    })));

  // Group sizes by orientation
  const portrait = visibleSizes.filter(v => v.metadata?.orientation === 'portrait');
  const landscape = visibleSizes.filter(v => v.metadata?.orientation === 'landscape');
  const square = visibleSizes.filter(v => v.metadata?.orientation === 'square');
  const custom = visibleSizes.filter(v => v.value === 'custom' || v.metadata?.orientation === 'custom');
  const uncategorized = visibleSizes.filter(v => !v.metadata?.orientation && v.value !== 'custom');

  // Determine which groups exist
  const hasPortrait = portrait.length > 0;
  const hasLandscape = landscape.length > 0;
  const hasSquare = square.length > 0;
  const hasCustom = custom.length > 0;
  const hasUncategorized = uncategorized.length > 0;

  // Show grouped tabs
  const defaultTab = hasLandscape ? 'landscape' : hasPortrait ? 'portrait' : hasSquare ? 'square' : hasCustom ? 'custom' : 'all';
  const selectedTab =
    landscape.some(v => v.value === selectedValue) ? 'landscape' :
    portrait.some(v => v.value === selectedValue) ? 'portrait' :
    square.some(v => v.value === selectedValue) ? 'square' :
    custom.some(v => v.value === selectedValue) ? 'custom' :
    uncategorized.some(v => v.value === selectedValue) ? 'all' :
    defaultTab;
  const [activeTab, setActiveTab] = useState(selectedTab);

  useEffect(() => {
    if (!selectedValue) return;
    setActiveTab((currentTab) => currentTab === selectedTab ? currentTab : selectedTab);
  }, [selectedTab, selectedValue]);

  const firstValidValue = (sizes: SizeValue[]) => {
    return sizes.find(size => !invalidValues.has(size.value))?.value || sizes[0]?.value;
  };

  const selectTabValue = (tab: string) => {
    const group =
      tab === 'landscape' ? landscape :
      tab === 'portrait' ? portrait :
      tab === 'square' ? square :
      tab === 'custom' ? custom :
      uncategorized;

    if (tab === 'custom') {
      const customValue = firstValidValue(group);
      if (customValue && selectedValue !== customValue) onChange(customValue);
      return;
    }

    if (group.length === 0 || group.some(size => size.value === selectedValue)) return;

    const nextValue = firstValidValue(group);
    if (nextValue) onChange(nextValue);
  };

  const renderSizeGrid = (sizes: SizeValue[]) => (
    <RadioGroup value={selectedValue || ''} onValueChange={onChange}>
      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {sizes.map((value, index) => {
          const isSelected = selectedValue === value.value;
          const displayLabel = formatMetricSizeLabel(value.label) || value.label;

          return (
            <label
              key={`${optionId}-${value.id}-${value.value}-${index}`}
              htmlFor={`${optionId}-${value.id}`}
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(value.value);
              }}
              className={`
                relative flex min-h-14 items-center justify-center rounded-lg border-2 p-2 text-center transition-all sm:p-3
                cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-600 has-[:focus-visible]:ring-offset-2
                ${isSelected ? 'border-blue-600 bg-blue-50 font-semibold shadow-md ring-1 ring-blue-600' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}
              `}
            >
              <RadioGroupItem
                value={value.value}
                id={`${optionId}-${value.id}`}
                className="sr-only"
              />
              {(() => {
                const [mainLabel, subLabel] = splitSizeLabel(
                  value.value.startsWith('custom:') && value.label
                    ? translatePrintLabel(displayLabel, locale)
                    : formatVirtualCustomSize(value.value) || translatePrintLabel(displayLabel, locale),
                );
                return (
                  <span className="flex min-w-0 flex-col items-center gap-0.5 px-3 text-center leading-snug">
                    <span className="text-xs sm:text-sm">{mainLabel}</span>
                    {subLabel && (
                      <span className="text-[11px] font-normal text-gray-500 sm:text-xs">{subLabel}</span>
                    )}
                    {Number(value.priceModifier || 0) !== 0 && (
                      <OptionPriceDelta modifier={Number(value.priceModifier)} locale={locale} className="mt-1" />
                    )}
                  </span>
                );
              })()}
              {isSelected && (
                <div className="absolute top-1 right-1">
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                </div>
              )}
            </label>
          );
        })}
      </div>
    </RadioGroup>
  );

  const renderCustomSizeInputs = () => onCustomSizeChange ? (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="mb-3 text-sm font-semibold text-slate-700">
        {locale === 'de' ? 'Eigene Masse eingeben' : locale === 'en' ? 'Enter your own size' : locale === 'fr' ? 'Saisissez vos dimensions' : 'Vul je eigen maten in'}
      </p>
      {customSizeHint ? (
        <p id={`${optionId}-custom-size-hint`} className="-mt-2 mb-3 text-xs leading-snug text-slate-500">{customSizeHint}</p>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        {(['width', 'height'] as const).map((dimension) => {
          const min = dimension === 'width' ? customSizeLimits?.minWidth : customSizeLimits?.minHeight;
          const max = dimension === 'width' ? customSizeLimits?.maxWidth : customSizeLimits?.maxHeight;
          const label = dimension === 'width'
            ? (locale === 'de' ? 'Breite' : locale === 'en' ? 'Width' : locale === 'fr' ? 'Largeur' : 'Breedte')
            : (locale === 'de' ? 'Hohe' : locale === 'en' ? 'Height' : locale === 'fr' ? 'Hauteur' : 'Hoogte');
          return (
            <label key={dimension} className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className="text-xs font-bold text-slate-600">{label} (cm)</span>
              <input
                type="number"
                inputMode="decimal"
                min={min}
                max={max}
                step="0.1"
                value={(dimension === 'width' ? customWidth : customHeight) ?? ''}
                onChange={(event) => onCustomSizeChange(dimension, event.target.value)}
                aria-describedby={customSizeHint ? `${optionId}-custom-size-hint` : undefined}
                className="h-11 w-full rounded-lg border border-slate-300 px-3 text-base outline-none ring-slate-900 focus:ring-2"
                placeholder={min && max ? `${min} - ${max}` : ''}
              />
              {min && max ? (
                <span className="text-[11px] text-slate-500">{`${min} - ${max} cm`}</span>
              ) : null}
            </label>
          );
        })}
      </div>
    </div>
  ) : null;

  // Plat raster (geen staand/liggend-tabs). De eigen-maatvelden staan anders
  // alleen in de tab "Eigen formaat"; zonder deze aanvulling kon je bij
  // bijvoorbeeld sleeves (3D-doosmaten) wel "Eigen formaat" kiezen, maar
  // nergens een maat invullen.
  const renderFlatGrid = () => {
    const showInputs = shouldShowInlineCustomSizeInputs({
      hasInputHandler: Boolean(onCustomSizeChange),
      selectedValue,
      customValues: custom.map((value) => String(value.value)),
    });
    if (!showInputs) return renderSizeGrid(visibleSizes);
    return (
      <div className="w-full space-y-4">
        {renderSizeGrid(visibleSizes)}
        {renderCustomSizeInputs()}
      </div>
    );
  };

  if (!groupByOrientation || sizeValuesAreShapes(visibleSizes)) {
    return renderFlatGrid();
  }

  // If no grouping exists, show all in a simple grid
  if (!hasPortrait && !hasLandscape && !hasSquare && !hasCustom) {
    return renderFlatGrid();
  }
  if (!hasPortrait && !hasLandscape && !hasCustom && hasUncategorized) {
    return renderFlatGrid();
  }
  const visibleOrientationGroupCount = [hasLandscape, hasPortrait, hasSquare, hasCustom]
    .filter(Boolean).length;
  if (visibleOrientationGroupCount <= 1 && !hasUncategorized) {
    return renderFlatGrid();
  }

  const tabButtonClass = (tab: string) => `
    inline-flex flex-1 basis-0 cursor-pointer items-center justify-center rounded-md border border-transparent py-1 font-medium
    transition-[color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1
    ${activeTab === tab ? 'bg-background text-gray-900 shadow-sm' : 'hover:bg-background/60'}
  `;

  return (
    <div className="w-full">
      {/* Flex in plaats van een vast kolomraster: het aantal tabs varieert van 1
          tot 4, dus grid-cols-2 liet drie tabs als 2+1 blok afbreken op 390px.
          Het aantalbadge verdwijnt onder sm: met badge bleef er per tab te
          weinig ruimte over en werd "Quadratisch" afgekapt tot "Quadr...". */}
      <div role="tablist" className="flex h-auto w-full gap-1 rounded-lg bg-muted p-1 text-muted-foreground">
        {hasLandscape && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'landscape'}
            data-state={activeTab === 'landscape' ? 'active' : 'inactive'}
            onClick={() => {
              setActiveTab('landscape');
              selectTabValue('landscape');
            }}
            className={`${tabButtonClass('landscape')} flex h-auto min-h-11 min-w-0 gap-1 whitespace-normal px-2 text-xs sm:gap-2 sm:text-sm`}
          >
            <span className="min-w-0 truncate">{translatePrintLabel('Liggend', locale)}</span>
            <Badge variant="secondary" className="hidden shrink-0 px-1.5 sm:inline-flex">{landscape.length}</Badge>
          </button>
        )}
        {hasPortrait && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'portrait'}
            data-state={activeTab === 'portrait' ? 'active' : 'inactive'}
            onClick={() => {
              setActiveTab('portrait');
              selectTabValue('portrait');
            }}
            className={`${tabButtonClass('portrait')} flex h-auto min-h-11 min-w-0 gap-1 whitespace-normal px-2 text-xs sm:gap-2 sm:text-sm`}
          >
            <span className="min-w-0 truncate">{translatePrintLabel('Staand', locale)}</span>
            <Badge variant="secondary" className="hidden shrink-0 px-1.5 sm:inline-flex">{portrait.length}</Badge>
          </button>
        )}
        {hasSquare && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'square'}
            data-state={activeTab === 'square' ? 'active' : 'inactive'}
            onClick={() => {
              setActiveTab('square');
              selectTabValue('square');
            }}
            className={`${tabButtonClass('square')} flex h-auto min-h-11 min-w-0 gap-1 whitespace-normal px-2 text-xs sm:gap-2 sm:text-sm`}
          >
            <span className="min-w-0 truncate">{translatePrintLabel('Vierkant', locale)}</span>
            <Badge variant="secondary" className="hidden shrink-0 px-1.5 sm:inline-flex">{square.length}</Badge>
          </button>
        )}
        {hasCustom && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'custom'}
            data-state={activeTab === 'custom' ? 'active' : 'inactive'}
            onClick={() => {
              setActiveTab('custom');
              selectTabValue('custom');
            }}
            className={`${tabButtonClass('custom')} flex h-auto min-h-11 min-w-0 px-2 text-xs sm:text-sm`}
          >
            <span className="min-w-0 truncate">{translatePrintLabel('Eigen formaat', locale)}</span>
          </button>
        )}
        {hasUncategorized && (
          // Zonder deze tab waren maten zonder richting (rond, 3D) onbereikbaar
          // zodra er andere tabs bestonden.
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'all'}
            data-state={activeTab === 'all' ? 'active' : 'inactive'}
            onClick={() => {
              setActiveTab('all');
              selectTabValue('all');
            }}
            className={`${tabButtonClass('all')} flex h-auto min-h-11 min-w-0 gap-1 whitespace-normal px-2 text-xs sm:gap-2 sm:text-sm`}
          >
            <span className="min-w-0 truncate">{locale === 'de' ? 'Weitere' : locale === 'en' ? 'Other' : locale === 'fr' ? 'Autres' : 'Overige'}</span>
            <Badge variant="secondary" className="hidden shrink-0 px-1.5 sm:inline-flex">{uncategorized.length}</Badge>
          </button>
        )}
      </div>

      {hasLandscape && activeTab === 'landscape' && (
        <div className="mt-4">
          {renderSizeGrid(landscape)}
        </div>
      )}

      {hasPortrait && activeTab === 'portrait' && (
        <div className="mt-4">
          {renderSizeGrid(portrait)}
        </div>
      )}

      {hasSquare && activeTab === 'square' && (
        <div className="mt-4">
          {renderSizeGrid(square)}
        </div>
      )}

      {hasCustom && activeTab === 'custom' && (
        <div className="mt-4 space-y-4">
          {renderSizeGrid(custom)}
          {renderCustomSizeInputs()}
        </div>
      )}

      {hasUncategorized && activeTab === 'all' && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-3">
            {locale === 'de' ? 'Weitere Formate' : locale === 'en' ? 'Other sizes' : locale === 'fr' ? 'Autres formats' : 'Overige formaten'}
          </p>
          {renderSizeGrid(uncategorized)}
        </div>
      )}
    </div>
  );
}
